import { useErrorHandler } from "@components/error-boundary/error-handling-context";
import { useEffect, useState } from "react";
import { Counter } from "@components/counter";
import { useTrackEvent } from "@utils/track-event/use-track-event";
import { MainPageEvents } from "@utils/track-event/track-event-list";
import { SubjectTypeProps } from "@custom-types/base-types";
import { formatSubjectType } from "@utils/data-display";
import { Accordion, CircularProgress } from "@flat-ui";
import { Box } from "@mui-wrapper";

const SLOW_MESSAGE_DELAY_MS = 3000;

interface Props<T> extends SubjectTypeProps {
  /** The search query string */
  searchQuery: string;

  /** The title of the search results */

  /** The function to fetch search results from backend */
  searchFn: (query: string) => Promise<T[]>;

  /** The component to render the search results */
  resultComponent: (results: T[]) => React.JSX.Element;
}

/** Showing results of different types matching search query */
export function SearchResults<T>({
  searchQuery,
  subjectType,
  searchFn,
  resultComponent,
}: Props<T>): React.JSX.Element {
  const { trackEvent } = useTrackEvent();
  const { handleErrorWithToast } = useErrorHandler();
  const [results, setResults] = useState<T[]>([]);
  const [isFetching, setIsFetching] = useState(false);
  const [shouldShowSlowMessage, setShouldShowSlowMessage] = useState(false);

  useEffect(() => {
    async function onSearchRequest(): Promise<void> {
      setIsFetching(true);
      setShouldShowSlowMessage(false);

      // Show message after 3 seconds if still fetching
      const slowTimer = setTimeout(() => {
        setShouldShowSlowMessage(true);
      }, SLOW_MESSAGE_DELAY_MS);

      try {
        const results = await searchFn(encodeURIComponent(searchQuery));
        setResults(results);
      } catch (error) {
        handleErrorWithToast({
          id: `SearchResults-${Date.now().toString()}`,
          title: `Could not search for ${subjectType.toLowerCase()}`,
          error,
        });
      } finally {
        clearTimeout(slowTimer);
        setIsFetching(false);
        setShouldShowSlowMessage(false);
      }
    }

    void onSearchRequest();
  }, [handleErrorWithToast, searchFn, searchQuery, subjectType]);

  function onChange(isExpanded: boolean): void {
    trackEvent({
      name: MainPageEvents.setCollapsesPanel,
      props: {
        target: subjectType,
        value: isExpanded ? "expand" : "collapse",
      },
    });
  }

  return (
    <Box
      sx={{
        mx: 2.5,
      }}
    >
      <Accordion
        isDisabled={isFetching || results.length === 0}
        onChange={onChange}
        title={
          <>
            {isFetching ? (
              <CircularProgress size={15} />
            ) : (
              <Counter counter={results.length} />
            )}
            <Box component="span" sx={{ ml: "4px" }}>
              {formatSubjectType(subjectType)}
            </Box>
            {shouldShowSlowMessage && (
              <Box component="span" sx={{ ml: "4px" }}>
                (Hang tight! Good things take a moment)
              </Box>
            )}
          </>
        }
      >
        {resultComponent(results)}
      </Accordion>
    </Box>
  );
}
